/**
 * Espace d'administration — écran « Rémunérations ».
 *
 * Les charges de personnel pèsent 71 % du budget : elles ne se pilotent pas
 * par un taux global. L'écran s'ouvre sur une vue d'ensemble, puis **une page
 * par salarié** — grille, historique, primes, hypothèses, coût de rupture —
 * parce qu'un arbitrage salarial se prend salarié par salarié, pas en moyenne.
 *
 * Les hypothèses sont rangées **par trajectoire** : « NAO minimale » et
 * « Maintien du pouvoir d'achat » n'ont pas les mêmes, et un scénario qui
 * choisit l'une n'hérite pas de l'autre. La trajectoire « Grille en l'état »
 * n'en accepte aucune : elle rend la grille de paie telle qu'elle est tenue,
 * échelons compris, et lui appliquer une hypothèse l'écraserait.
 */

import {
  TRAJECTOIRES_SALARIALES,
  dateFr,
  entier,
  euros,
  eurosSigne,
  keuros,
  nomAffiche,
  pourcent,
  type HypotheseSalariale,
  type Salarie,
} from '@scc/core';
import {
  creerSalarie,
  effacerHypotheses,
  enregistrerHypotheses,
  supprimerSalarie,
  type Charge,
  type Identite,
  type SalarieSaisi,
} from '../api.js';
import { h, vider } from '../dom.js';
import { TEINTES, graphiqueLignes, legende } from '../graphiques.js';
import { badge, enteteSection, insight, kpi, noteLecture, regleAccent } from '../composants.js';

/** Clé d'une hypothèse dans le brouillon. */
const cle = (salarie: string, exercice: number): string => `${salarie}|${exercice}`;

export function vueRemunerations(
  d: Charge,
  identite: Identite,
  recharger: (trajectoire: string) => void,
): HTMLElement {
  const estDirection = identite.role === 'administration';
  const paie = d.paie;

  if (!paie) {
    return h(
      'div',
      { classe: 'page', id: 'page-remunerations', role: 'region', 'aria-label': 'Rémunérations' },
      h(
        'section',
        {},
        enteteSection(1, 'Rémunérations', 'Trajectoires salariales par salarié'),
        insight('warning', '⚠', 'Aucune donnée de paie n’est chargée : la grille de salaires est absente de la base.'),
      ),
    );
  }

  const exercices = paie.exercicesProjetes;
  const decisions = paie.decisions;

  /** Trajectoire arbitrée à l'écran ; « grille » n'accepte pas d'hypothèse. */
  let trajectoire =
    paie.trajectoire !== 'grille'
      ? paie.trajectoire
      : (TRAJECTOIRES_SALARIALES.find((t) => t.cle !== 'grille')?.cle ?? 'nao_minimale');

  /** Brouillon local, indexé par salarié et exercice. */
  let brouillon = new Map<string, HypotheseSalariale>();
  const chargerBrouillon = (): void => {
    brouillon = new Map(
      paie.hypotheses
        .filter((x) => x.trajectoire === trajectoire)
        .map((x) => [cle(x.salarie, x.exercice), x]),
    );
  };
  chargerBrouillon();

  const retour = h('div', {});
  const dire = (texte: string, ok: boolean): void => {
    vider(retour);
    retour.appendChild(h('p', { classe: `retour-editeur ${ok ? 'ok' : 'ko'}`, role: 'status' }, texte));
  };

  const valeur = (salarie: string, exercice: number): HypotheseSalariale => {
    const existante = brouillon.get(cle(salarie, exercice));
    if (existante) return existante;
    const modele = TRAJECTOIRES_SALARIALES.find((t) => t.cle === trajectoire);
    return {
      trajectoire,
      salarie,
      exercice,
      revalorisation: modele?.revalorisationAnnuelle ?? 0,
      mesureIndividuelle: 0,
      dernierMois: null,
    };
  };
  const poser = (x: HypotheseSalariale): void => {
    brouillon.set(cle(x.salarie, x.exercice), x);
  };

  /** Taux annuel issu des décisions du bureau pour un salarié. */
  const tauxDecision = (id: string): number =>
    decisions.taux.find((t) => t.salarie === id)?.taux ?? 0;

  // ── Grille d'hypothèses d'un salarié ────────────────────────────────────
  const grilleHypotheses = (id: string, titre: string): HTMLElement =>
    h(
      'div',
      { classe: 'enveloppe-table' },
      h(
        'table',
        { classe: 'table-scenario' },
        h('caption', { classe: 'invisible' }, `Hypothèses salariales — ${titre}`),
        h(
          'thead',
          {},
          h(
            'tr',
            {},
            h('th', { scope: 'col' }, 'Exercice'),
            h('th', { scope: 'col' }, 'Revalorisation générale'),
            h('th', { scope: 'col' }, 'Mesure individuelle mensuelle'),
            h('th', { scope: 'col' }, 'Dernier mois payé'),
          ),
        ),
        h(
          'tbody',
          {},
          ...exercices.map((a) => {
            const v = valeur(id, a);
            return h(
              'tr',
              {},
              h('th', { scope: 'row' }, String(a)),
              h(
                'td',
                {},
                h('input', {
                  type: 'number',
                  step: 0.1,
                  min: -100,
                  max: 100,
                  value: (v.revalorisation * 100).toFixed(2),
                  'aria-label': `Revalorisation ${titre} en ${a}, en pourcentage`,
                  readonly: !estDirection,
                  surChange: (ev) => {
                    const t = Number((ev.currentTarget as HTMLInputElement).value) / 100;
                    if (Number.isFinite(t)) poser({ ...v, revalorisation: t });
                  },
                }),
                ' %',
              ),
              h(
                'td',
                {},
                h('input', {
                  type: 'number',
                  step: 10,
                  value: String(v.mesureIndividuelle),
                  'aria-label': `Mesure individuelle ${titre} en ${a}, en euros mensuels`,
                  readonly: !estDirection,
                  surChange: (ev) => {
                    const m = Number((ev.currentTarget as HTMLInputElement).value);
                    if (Number.isFinite(m)) poser({ ...v, mesureIndividuelle: m });
                  },
                }),
                ' € / mois',
              ),
              h(
                'td',
                {},
                h(
                  'select',
                  {
                    'aria-label': `Dernier mois payé pour ${titre} en ${a}`,
                    disabled: !estDirection,
                    surChange: (ev) => {
                      const s = (ev.currentTarget as HTMLSelectElement).value;
                      poser({ ...v, dernierMois: s === '' ? null : Number(s) });
                    },
                  },
                  h('option', { value: '', ...(v.dernierMois === null ? { selected: true } : {}) }, 'toute l’année'),
                  ...Array.from({ length: 12 }, (_, i) => i + 1).map((m) =>
                    h(
                      'option',
                      { value: String(m), ...(v.dernierMois === m ? { selected: true } : {}) },
                      `jusqu’au mois ${m}`,
                    ),
                  ),
                ),
              ),
            );
          }),
        ),
      ),
    );

  // ── Barre d'enregistrement, commune à toutes les pages ──────────────────
  const barreEnregistrement = (): HTMLElement =>
    h(
      'div',
      { classe: 'barre-actions' },
      h('span', { classe: 'titre' }, 'Trajectoire à arbitrer'),
      h(
        'select',
        {
          'aria-label': 'Trajectoire salariale à arbitrer',
          surChange: (ev) => {
            trajectoire = (ev.currentTarget as HTMLSelectElement).value;
            chargerBrouillon();
            vider(retour);
            dessiner();
          },
        },
        // « Grille en l'état » est exclue : elle n'accepte pas d'hypothèse.
        ...TRAJECTOIRES_SALARIALES.filter((t) => t.revalorisationAnnuelle !== null).map((t) =>
          h('option', { value: t.cle, ...(t.cle === trajectoire ? { selected: true } : {}) }, t.libelle),
        ),
      ),
      ...(estDirection
        ? [
            h(
              'button',
              {
                classe: 'btn-action',
                type: 'button',
                surClick: (ev) => {
                  const b = ev.currentTarget as HTMLButtonElement;
                  void (async () => {
                    b.disabled = true;
                    try {
                      const lot = [...brouillon.values()].map((x) => ({ ...x, trajectoire }));
                      await enregistrerHypotheses(trajectoire, lot);
                      dire(
                        `${lot.length} hypothèse(s) enregistrée(s) sur « ${trajectoire} ». ` +
                          'La masse salariale se recalcule au rechargement.',
                        true,
                      );
                      recharger(trajectoire);
                    } catch (e) {
                      dire(e instanceof Error ? e.message : 'Enregistrement refusé.', false);
                    } finally {
                      b.disabled = false;
                    }
                  })();
                },
              },
              'Enregistrer et appliquer',
            ),
            h(
              'button',
              {
                classe: 'btn-action',
                type: 'button',
                surClick: (ev) => {
                  const b = ev.currentTarget as HTMLButtonElement;
                  void (async () => {
                    b.disabled = true;
                    try {
                      await effacerHypotheses(trajectoire);
                      brouillon = new Map();
                      dire(`Hypothèses de « ${trajectoire} » remises à zéro.`, true);
                      recharger(trajectoire);
                    } catch (e) {
                      dire(e instanceof Error ? e.message : 'Remise à zéro refusée.', false);
                    } finally {
                      b.disabled = false;
                    }
                  })();
                },
              },
              'Remettre à zéro',
            ),
          ]
        : []),
    );

  // ── Vue d'ensemble ──────────────────────────────────────────────────────
  const courante = paie.exerciceCourant;
  const serieProjetee = paie.serie.filter((m) => m.exercice >= d.meta.exerciceCourant);
  const derniere = serieProjetee[serieProjetee.length - 1];

  const vueEnsemble = (): HTMLElement => {
    const cible = decisions.cible;
    const referent = paie.salaries.find((s) => s.nom.toUpperCase() === cible.nom);
    const atteint = referent
      ? (paie.serie.find((m) => m.exercice === cible.exercice)?.situations.find(
          (s) => s.salarie.id === referent.id,
        )?.brutMensuelFin ?? 0)
      : 0;

    return h(
      'div',
      {},
      h(
        'div',
        { classe: 'kpi-grid' },
        kpi('neutral', keuros(courante.masseChargee), null, `Masse chargée ${courante.exercice}`, {
          sens: 'neutre',
          texte: `${entier(courante.effectif)} salariés · taux ${pourcent(courante.taux, 1)}${courante.tauxConstate ? ' constaté' : ' estimé'}`,
        }),
        kpi(
          'neutral',
          keuros(derniere?.masseChargee ?? 0),
          null,
          `Masse chargée projetée ${derniere?.exercice ?? ''}`,
          {
            sens: (derniere?.masseChargee ?? 0) > courante.masseChargee ? 'up' : 'down',
            texte: `${eurosSigne((derniere?.masseChargee ?? 0) - courante.masseChargee)} sur la période`,
          },
        ),
        kpi(
          'neutral',
          paie.trajectoiresDisponibles.find((t) => t.cle === paie.trajectoire)?.libelle ?? '—',
          null,
          'Trajectoire servie',
          { sens: 'neutre', texte: 'celle que l’application affiche aujourd’hui' },
        ),
        kpi(
          atteint >= cible.brutMensuel ? 'progress' : 'warning',
          euros(atteint),
          null,
          `Cible ${cible.nom} au 1ᵉʳ janvier ${cible.exercice}`,
          {
            sens: atteint >= cible.brutMensuel ? 'up' : 'down',
            texte: `${euros(cible.brutMensuel)} décidés — écart ${eurosSigne(atteint - cible.brutMensuel)}`,
          },
        ),
      ),

      insight(
        'info',
        '§',
        `**${decisions.libelle}.** ${decisions.reference}. La décision ne fixe pas trois taux mais une cible et ` +
          `une chaîne de proportions : ${cible.nom} atteint ${euros(cible.brutMensuel)} bruts mensuels au ` +
          `1ᵉʳ janvier ${cible.exercice} ; les autres sont augmentés d’une fraction de ce taux. Les taux ci-dessous ` +
          `s’en déduisent, ils ne sont pas saisis.`,
      ),

      h(
        'div',
        { classe: 'enveloppe-table' },
        h(
          'table',
          { classe: 'comp-table encadre-tableau' },
          h('caption', { classe: 'invisible' }, 'Taux issus des décisions du bureau'),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, 'Salarié'),
              h('th', { scope: 'col' }, 'Part du taux de référence'),
              h('th', { scope: 'col' }, 'Taux annuel déduit'),
              h('th', { scope: 'col' }, `Brut mensuel fin ${d.meta.exerciceCourant}`),
              h('th', { scope: 'col' }, `Brut mensuel fin ${cible.exercice}`),
            ),
          ),
          h(
            'tbody',
            {},
            ...paie.salaries.map((s) => {
              const part = decisions.parts[s.nom.toUpperCase()] ?? decisions.partParDefaut;
              const debut =
                paie.serie
                  .find((m) => m.exercice === d.meta.exerciceCourant)
                  ?.situations.find((x) => x.salarie.id === s.id)?.brutMensuelFin ?? 0;
              const fin =
                paie.serie
                  .find((m) => m.exercice === cible.exercice)
                  ?.situations.find((x) => x.salarie.id === s.id)?.brutMensuelFin ?? 0;
              return h(
                'tr',
                {},
                h('th', { scope: 'row' }, nomAffiche(s)),
                h('td', { classe: 'num' }, pourcent(part, 0)),
                h('td', { classe: 'num' }, pourcent(tauxDecision(s.id), 3)),
                h('td', { classe: 'num' }, euros(debut)),
                h('td', { classe: 'num' }, euros(fin)),
              );
            }),
          ),
        ),
      ),
      noteLecture(
        `Les taux ci-dessus s’appliquent lorsque la trajectoire « ${decisions.libelle} » est servie. ` +
          `Les autres mesures de la même décision, sans effet sur la grille : ` +
          decisions.autresMesures.join(' '),
      ),

      regleAccent('Masse salariale projetée'),
      graphiqueLignes(
        serieProjetee.map((m) => m.exercice),
        [
          { nom: 'Masse chargée', couleur: TEINTES.charcoal, valeurs: serieProjetee.map((m) => m.masseChargee) },
          { nom: 'Brut', couleur: TEINTES.blue, valeurs: serieProjetee.map((m) => m.brut) },
          {
            nom: 'Charges patronales',
            couleur: TEINTES.amber,
            valeurs: serieProjetee.map((m) => m.chargesPatronales),
          },
        ],
        {
          titre: 'Masse salariale projetée sous la trajectoire servie',
          enteteAbscisse: 'Exercice',
          unite: 'euros',
          depuisZero: true,
        },
      ),
      legende([
        { nom: 'Masse chargée', couleur: TEINTES.charcoal },
        { nom: 'Brut', couleur: TEINTES.blue },
        { nom: 'Charges patronales', couleur: TEINTES.amber },
      ]),
      noteLecture(
        `Le taux de charges patronales est constaté sur les écritures de paie : ${pourcent(courante.taux, 2)}` +
          (courante.tauxConstate ? '.' : ', valeur de repli faute d’écritures exploitables.') +
          ` La masse chargée comprend le brut, les charges patronales, les titres-restaurant ` +
          `(${euros(courante.titresRestaurant)}) et les primes (${euros(courante.primes)}).`,
      ),

      regleAccent('Mesure générale'),
      h(
        'p',
        { classe: 'text-block' },
        'La mesure générale s’applique à tout salarié qui n’a pas d’hypothèse propre sur l’exercice. ' +
          'Une hypothèse individuelle la **remplace**, elle ne s’y ajoute pas.',
      ),
      grilleHypotheses('*', 'Mesure générale'),
      ...(estDirection ? [regleAccent('Ajouter un salarié'), formulaireSalarie()] : []),
    );
  };

  // ── Formulaire d'entrée d'un salarié ────────────────────────────────────
  //
  // Le brut mensuel amorce la grille du mois d'entrée à décembre : sans point
  // de départ, le moteur de paie n'aurait rien à projeter et le salarié
  // n'existerait que sur sa fiche.
  const formulaireSalarie = (): HTMLElement => {
    const saisie: SalarieSaisi = {
      nom: '',
      prenom: '',
      role: 'salarie',
      titre: null,
      dateEntree: `${d.meta.exerciceCourant}-01-01`,
      dateSortie: null,
      brutMensuel: 0,
      groupe: null,
      echelon: null,
    };
    let brouillonSalarie = saisie;

    const champ = (
      libelle: string,
      attrs: Record<string, string | number | boolean | null>,
      surSaisie: (v: string) => void,
    ): HTMLElement =>
      h(
        'label',
        {},
        libelle,
        h('input', {
          ...attrs,
          surChange: (ev) => { surSaisie((ev.currentTarget as HTMLInputElement).value); },
        }),
      );

    return h(
      'div',
      {},
      h(
        'div',
        { classe: 'editeur-champs' },
        champ('Nom', { type: 'text', maxlength: 80 }, (v) => {
          brouillonSalarie = { ...brouillonSalarie, nom: v.toUpperCase() };
        }),
        champ('Prénom', { type: 'text', maxlength: 80 }, (v) => {
          brouillonSalarie = { ...brouillonSalarie, prenom: v };
        }),
        h(
          'label',
          {},
          'Rôle',
          h(
            'select',
            {
              surChange: (ev) => {
                brouillonSalarie = {
                  ...brouillonSalarie,
                  role: (ev.currentTarget as HTMLSelectElement).value as SalarieSaisi['role'],
                };
              },
            },
            h('option', { value: 'salarie' }, 'Salarié'),
            h('option', { value: 'rh_compta' }, 'Cadre — RH et comptabilité'),
            h('option', { value: 'direction' }, 'Direction'),
          ),
        ),
        champ('Intitulé du poste', { type: 'text', maxlength: 120 }, (v) => {
          brouillonSalarie = { ...brouillonSalarie, titre: v === '' ? null : v };
        }),
        champ('Date d’entrée', { type: 'date', value: saisie.dateEntree }, (v) => {
          brouillonSalarie = { ...brouillonSalarie, dateEntree: v };
        }),
        champ('Brut mensuel', { type: 'number', step: 10, min: 0 }, (v) => {
          const n = Number(v);
          if (Number.isFinite(n)) brouillonSalarie = { ...brouillonSalarie, brutMensuel: n };
        }),
        champ('Groupe', { type: 'number', step: 1, min: 1, max: 20 }, (v) => {
          const n = Number(v);
          brouillonSalarie = { ...brouillonSalarie, groupe: Number.isFinite(n) && v !== '' ? n : null };
        }),
        champ('Échelon', { type: 'number', step: 1, min: 1, max: 20 }, (v) => {
          const n = Number(v);
          brouillonSalarie = { ...brouillonSalarie, echelon: Number.isFinite(n) && v !== '' ? n : null };
        }),
      ),
      h(
        'div',
        { classe: 'barre-actions' },
        h(
          'button',
          {
            classe: 'btn-action',
            type: 'button',
            surClick: (ev) => {
              const b = ev.currentTarget as HTMLButtonElement;
              void (async () => {
                b.disabled = true;
                try {
                  const r = await creerSalarie(brouillonSalarie);
                  dire(
                    `Salarié créé, grille amorcée sur ${r.nbPoints} mois de ${d.meta.exerciceCourant}. ` +
                      'Rechargement en cours.',
                    true,
                  );
                  recharger(paie.trajectoire);
                } catch (e) {
                  dire(e instanceof Error ? e.message : 'Création refusée.', false);
                } finally {
                  b.disabled = false;
                }
              })();
            },
          },
          'Créer le salarié',
        ),
      ),
      noteLecture(
        `La grille est amorcée du mois d’entrée à décembre ${d.meta.exerciceCourant}, au brut saisi. Les ` +
          `exercices suivants sont projetés par la trajectoire servie. Un salarié entré ici survit au ` +
          `réamorçage depuis l’instantané : seules les données venues des sources y sont remplacées.`,
      ),
    );
  };

  // ── Page d'un salarié ───────────────────────────────────────────────────
  const pageSalarie = (s: Salarie): HTMLElement => {
    /** Situation de ce salarié sur chaque exercice de la série. */
    const situations = paie.serie
      .map((m) => ({ exercice: m.exercice, s: m.situations.find((x) => x.salarie.id === s.id) }))
      .filter((x): x is { exercice: number; s: NonNullable<typeof x.s> } => x.s !== undefined);

    const actuelle = situations.find((x) => x.exercice === d.meta.exerciceCourant)?.s;
    const finale = situations[situations.length - 1]?.s;
    const primes = paie.primes.filter((p) => p.salarie === s.id);
    const ruptures = paie.rupture
      .map((r) => ({ exercice: r.exercice, c: r.parSalarie.find((x) => x.salarie.id === s.id) }))
      .filter((x): x is { exercice: number; c: NonNullable<typeof x.c> } => x.c !== undefined);

    return h(
      'div',
      {},
      h(
        'div',
        { classe: 'kpi-grid' },
        kpi('neutral', euros(actuelle?.brutMensuelFin ?? 0), null, `Brut mensuel fin ${d.meta.exerciceCourant}`, {
          sens: (actuelle?.progression ?? 0) > 0 ? 'up' : 'neutre',
          texte:
            actuelle?.progression !== null && actuelle?.progression !== undefined
              ? `${pourcent(actuelle.progression, 2)} sur un an`
              : 'progression inconnue',
        }),
        kpi('neutral', euros(actuelle?.coutEmployeur ?? 0), null, `Coût employeur ${d.meta.exerciceCourant}`, {
          sens: 'neutre',
          texte: `brut ${euros(actuelle?.brutAnnuel ?? 0)} + charges ${euros(actuelle?.chargesPatronales ?? 0)}`,
        }),
        kpi('neutral', euros(finale?.brutMensuelFin ?? 0), null, `Brut mensuel projeté fin ${finale?.exercice ?? ''}`, {
          sens: 'up',
          texte: `taux décidé ${pourcent(tauxDecision(s.id), 3)} par an`,
        }),
        kpi(
          'warning',
          euros(ruptures[ruptures.length - 1]?.c.coutTotal ?? 0),
          null,
          `Coût de rupture fin ${ruptures[ruptures.length - 1]?.exercice ?? ''}`,
          {
            sens: 'up',
            texte: `${entier(Math.floor(ruptures[ruptures.length - 1]?.c.anciennete ?? 0))} ans d’ancienneté`,
          },
        ),
      ),

      h(
        'p',
        { classe: 'text-block' },
        `**${s.titre ?? 'Salarié'}.** Entré${s.dateEntree ? ` le ${dateFr(s.dateEntree)}` : ''}` +
          (s.dateSortie ? `, sorti le ${dateFr(s.dateSortie)}` : '') +
          `${s.actif ? '' : ' — contrat terminé'}. ` +
          `Groupe ${actuelle?.groupe ?? '—'}, échelon ${actuelle?.echelon ?? '—'}. ` +
          `Part du taux de référence des décisions du bureau : ` +
          `${pourcent(decisions.parts[s.nom.toUpperCase()] ?? decisions.partParDefaut, 0)}.`,
      ),

      regleAccent('Évolution du brut mensuel'),
      graphiqueLignes(
        situations.map((x) => x.exercice),
        [
          {
            nom: 'Brut mensuel en fin d’exercice',
            couleur: TEINTES.charcoal,
            valeurs: situations.map((x) => x.s.brutMensuelFin),
          },
        ],
        {
          titre: `Brut mensuel — ${nomAffiche(s)}`,
          enteteAbscisse: 'Exercice',
          unite: 'euros',
          depuisZero: true,
        },
      ),

      h(
        'div',
        { classe: 'enveloppe-table' },
        h(
          'table',
          { classe: 'comp-table encadre-tableau' },
          h('caption', { classe: 'invisible' }, `Situation par exercice — ${nomAffiche(s)}`),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, 'Exercice'),
              h('th', { scope: 'col' }, 'Brut mensuel fin'),
              h('th', { scope: 'col' }, 'Brut annuel'),
              h('th', { scope: 'col' }, 'Progression'),
              h('th', { scope: 'col' }, 'Charges patronales'),
              h('th', { scope: 'col' }, 'Primes'),
              h('th', { scope: 'col' }, 'Coût employeur'),
            ),
          ),
          h(
            'tbody',
            {},
            ...situations.map((x) =>
              h(
                'tr',
                { classe: x.exercice === d.meta.exerciceCourant ? 'ligne-alerte' : '' },
                h('th', { scope: 'row' }, String(x.exercice)),
                h('td', { classe: 'num' }, euros(x.s.brutMensuelFin)),
                h('td', { classe: 'num' }, euros(x.s.brutAnnuel)),
                h('td', { classe: 'num' }, x.s.progression === null ? '—' : pourcent(x.s.progression, 2)),
                h('td', { classe: 'num' }, euros(x.s.chargesPatronales)),
                h('td', { classe: 'num' }, euros(x.s.primes.total)),
                h('td', { classe: 'num' }, euros(x.s.coutEmployeur)),
              ),
            ),
          ),
        ),
      ),
      noteLecture(
        `L'exercice en cours est signalé. Au-delà, la grille est **projetée** sous la trajectoire servie ` +
          `— « ${paie.trajectoiresDisponibles.find((t) => t.cle === paie.trajectoire)?.libelle ?? paie.trajectoire} ».`,
      ),

      regleAccent('Primes versées'),
      primes.length === 0
        ? h('p', { classe: 'note-lecture' }, 'Aucune prime enregistrée pour ce salarié.')
        : h(
            'table',
            { classe: 'comp-table encadre-tableau' },
            h('caption', { classe: 'invisible' }, `Primes — ${nomAffiche(s)}`),
            h(
              'thead',
              {},
              h(
                'tr',
                {},
                h('th', { scope: 'col' }, 'Mois'),
                h('th', { scope: 'col' }, 'Type'),
                h('th', { scope: 'col' }, 'Montant'),
                h('th', { scope: 'col' }, 'Exonération'),
                h('th', { scope: 'col' }, 'Motif'),
              ),
            ),
            h(
              'tbody',
              {},
              ...primes.map((p) =>
                h(
                  'tr',
                  {},
                  h('th', { scope: 'row' }, p.moisVersement),
                  h('td', {}, p.type),
                  h('td', { classe: 'num' }, euros(p.montantBrut)),
                  h(
                    'td',
                    {},
                    badge(p.exonerationPresumee ? 'favorable' : 'vigilance', p.exonerationPresumee ? 'présumée' : 'chargée'),
                  ),
                  h('td', {}, p.motif ?? '—'),
                ),
              ),
            ),
          ),

      regleAccent('Coût de rupture, exercice par exercice'),
      h(
        'div',
        { classe: 'enveloppe-table' },
        h(
          'table',
          { classe: 'comp-table encadre-tableau' },
          h('caption', { classe: 'invisible' }, `Coût de rupture — ${nomAffiche(s)}`),
          h(
            'thead',
            {},
            h(
              'tr',
              {},
              h('th', { scope: 'col' }, 'Au 31 décembre'),
              h('th', { scope: 'col' }, 'Ancienneté'),
              h('th', { scope: 'col' }, 'Salaire de référence'),
              h('th', { scope: 'col' }, 'Indemnité'),
              h('th', { scope: 'col' }, 'Préavis'),
              h('th', { scope: 'col' }, 'Congés payés'),
              h('th', { scope: 'col' }, 'Charges'),
              h('th', { scope: 'col' }, 'Coût total'),
            ),
          ),
          h(
            'tbody',
            {},
            ...ruptures.map((x) =>
              h(
                'tr',
                {},
                h('th', { scope: 'row' }, String(x.exercice)),
                h('td', { classe: 'num' }, `${x.c.anciennete.toFixed(1).replace('.', ',')} ans`),
                h('td', { classe: 'num' }, euros(x.c.salaireReference)),
                h('td', { classe: 'num' }, euros(x.c.indemniteLicenciement)),
                h('td', { classe: 'num' }, euros(x.c.preavis)),
                h('td', { classe: 'num' }, euros(x.c.congesPayes)),
                h('td', { classe: 'num' }, euros(x.c.chargesPatronales)),
                h('td', { classe: 'num' }, euros(x.c.coutTotal)),
              ),
            ),
          ),
        ),
      ),
      insight(
        'info',
        '⚖',
        `**Ce coût commande le seuil d'alerte.** Le seuil vaut le plancher de trésorerie augmenté du coût de ` +
          `rupture du salarié le plus cher au 31 décembre. ` +
          (d.seuils.applicables[d.meta.exerciceCourant]?.salarie === nomAffiche(s)
            ? `C'est actuellement ce salarié qui le détermine.`
            : `Ce n'est pas ce salarié qui le détermine aujourd'hui, mais ` +
              `${d.seuils.applicables[d.meta.exerciceCourant]?.salarie ?? '—'}.`),
      ),

      ...(estDirection
        ? [
            regleAccent('Fin de contrat'),
            h(
              'p',
              { classe: 'text-block' },
              'Un salarié qui figure dans les écritures de paie ne s’efface pas : il a existé, et les ' +
                'exercices clos en portent la trace. On lui renseigne une **date de sortie**, qui interrompt ' +
                'la projection sans réécrire le passé. La suppression n’est possible que pour un salarié ' +
                'saisi ici et sans aucune écriture.',
            ),
            h(
              'div',
              { classe: 'barre-actions' },
              h(
                'button',
                {
                  classe: 'btn-action',
                  type: 'button',
                  surClick: (ev) => {
                    const b = ev.currentTarget as HTMLButtonElement;
                    void (async () => {
                      b.disabled = true;
                      try {
                        await supprimerSalarie(s.id);
                        dire(`${nomAffiche(s)} supprimé.`, true);
                        recharger(paie.trajectoire);
                      } catch (e) {
                        dire(e instanceof Error ? e.message : 'Suppression refusée.', false);
                      } finally {
                        b.disabled = false;
                      }
                    })();
                  },
                },
                'Supprimer ce salarié',
              ),
            ),
          ]
        : []),

      regleAccent('Hypothèses propres à ce salarié'),
      h(
        'p',
        { classe: 'text-block' },
        'Chaque exercice part du brut du dernier mois de l’exercice précédent, lui applique la ' +
          'revalorisation, puis ajoute la mesure individuelle. Un « dernier mois payé » interrompt la paie ' +
          'à cette date : c’est ainsi qu’on chiffre un départ.',
      ),
      grilleHypotheses(s.id, nomAffiche(s)),
    );
  };

  // ── Sous-navigation ─────────────────────────────────────────────────────
  const onglets = [
    { cle: 'ensemble', libelle: 'Vue d’ensemble' },
    ...paie.salaries.map((s) => ({ cle: s.id, libelle: nomAffiche(s) })),
  ];
  let ongletCourant = 'ensemble';

  const zoneOnglets = h('div', { classe: 'barre-actions', role: 'tablist' });
  const zoneContenu = h('div', {});

  const dessiner = (): void => {
    vider(zoneOnglets);
    zoneOnglets.appendChild(h('span', { classe: 'titre' }, 'Fiches'));
    for (const o of onglets) {
      zoneOnglets.appendChild(
        h(
          'button',
          {
            classe: 'btn-action',
            type: 'button',
            role: 'tab',
            'aria-selected': o.cle === ongletCourant ? 'true' : 'false',
            surClick: () => {
              ongletCourant = o.cle;
              dessiner();
            },
          },
          o.libelle,
        ),
      );
    }

    vider(zoneContenu);
    zoneContenu.appendChild(barreEnregistrement());
    zoneContenu.appendChild(retour);
    const s = paie.salaries.find((x) => x.id === ongletCourant);
    zoneContenu.appendChild(s ? pageSalarie(s) : vueEnsemble());
  };

  dessiner();

  return h(
    'div',
    { classe: 'page', id: 'page-remunerations', role: 'region', 'aria-label': 'Rémunérations' },
    h(
      'section',
      {},
      enteteSection(1, 'Rémunérations', 'Une fiche par salarié, et les décisions qui les commandent'),
      zoneOnglets,
      zoneContenu,
    ),
  );
}
